import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config";
import { Copy, Check, ExternalLink, MessageCircle } from "lucide-react";
import styles from "./PaymentMethodSelector.module.css";

export type PaymentMethod = "cod" | "instapay" | "wallet";

interface PaymentSettings {
  instapayLink: string;
  walletNumber: string;
  walletName: string;
  whatsappNumber: string;
}

interface PaymentMethodSelectorProps {
  value: PaymentMethod;
  onChange: (method: PaymentMethod) => void;
}

export function PaymentMethodSelector({
  value,
  onChange,
}: PaymentMethodSelectorProps) {
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/payment-settings`);
        const data = await response.json();
        setSettings(data.paymentSettings || null);
      } catch (error) {
        console.error("Error fetching payment settings:", error);
      }
    };

    fetchSettings();
  }, []);

  const handleCopy = () => {
    if (!settings?.walletNumber) return;
    navigator.clipboard.writeText(settings.walletNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappLink = settings?.whatsappNumber
    ? `https://wa.me/${settings.whatsappNumber.replace(/\D/g, "")}`
    : undefined;

  return (
    <div className={styles.paymentMethods}>
      <label className={styles.methodCard}>
        <input
          type="radio"
          name="paymentMethod"
          value="cod"
          checked={value === "cod"}
          onChange={() => onChange("cod")}
        />
        <div className={styles.methodContent}>
          <span className={styles.methodLabel}>Cash on Delivery</span>
          <span className={styles.methodSubLabel}>
            Pay in cash when your order arrives
          </span>
        </div>
      </label>

      <label className={styles.methodCard}>
        <input
          type="radio"
          name="paymentMethod"
          value="instapay"
          checked={value === "instapay"}
          onChange={() => onChange("instapay")}
        />
        <div className={styles.methodContent}>
          <span className={styles.methodLabel}>Instapay Transfer</span>
          <span className={styles.methodSubLabel}>
            Pay instantly via Instapay
          </span>
        </div>
      </label>

      <label className={styles.methodCard}>
        <input
          type="radio"
          name="paymentMethod"
          value="wallet"
          checked={value === "wallet"}
          onChange={() => onChange("wallet")}
        />
        <div className={styles.methodContent}>
          <span className={styles.methodLabel}>Mobile Wallet Transfer</span>
          <span className={styles.methodSubLabel}>
            Send money to our wallet number
          </span>
        </div>
      </label>

      {value === "instapay" && (
        <div className={styles.methodDetails}>
          {settings?.instapayLink ? (
            <a
              href={settings.instapayLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionLink}
            >
              <ExternalLink size={16} />
              Pay via Instapay
            </a>
          ) : (
            <p className={styles.methodNote}>
              Instapay link is not available right now — please choose
              another payment method or contact us.
            </p>
          )}
          <p className={styles.methodNote}>
            After transferring, take a screenshot and send it to us on
            WhatsApp so we can confirm your payment.
          </p>
          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.actionLink} ${styles.whatsappLink}`}
            >
              <MessageCircle size={16} />
              Send Screenshot on WhatsApp
            </a>
          )}
        </div>
      )}

      {value === "wallet" && (
        <div className={styles.methodDetails}>
          {settings?.walletNumber ? (
            <div className={styles.walletNumberRow}>
              <div className={styles.walletNumberInfo}>
                <span className={styles.walletNumber}>
                  {settings.walletNumber}
                </span>
                {settings.walletName && (
                  <span className={styles.walletName}>
                    {settings.walletName}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className={styles.copyBtn}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          ) : (
            <p className={styles.methodNote}>
              Wallet number is not available right now — please choose
              another payment method or contact us.
            </p>
          )}
          <p className={styles.methodNote}>
            After transferring, take a screenshot and send it to us on
            WhatsApp so we can confirm your payment.
          </p>
          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.actionLink} ${styles.whatsappLink}`}
            >
              <MessageCircle size={16} />
              Send Screenshot on WhatsApp
            </a>
          )}
        </div>
      )}
    </div>
  );
}